import { FunctionComponent, useCallback } from "react";

import { AlertViewModel } from "@ppb/the-wall-common/types";
import { FreeBets, NudgesNumberInputField, PrimaryButton, QuickStakes, FixedNumberInputField } from "@ppb/the-wall-web";
import { Alerts } from "../../../Alerts/Alerts.web";

import { PNLAndWhatIf } from "../../../PNLAndWhatIf/PNLAndWhatIf.web";
import { PNLAndWhatIfSize } from "../../../PNLAndWhatIf/PNLAndWhatIf.types";
import { OutcomeBetSegments } from "../../../OutcomeBetSegments/OutcomeBetSegments.web";
import { BetSegmentsSize } from "../../../BetSegments/BetSegments.types";

import { BspOddsLimit } from "../../../BspOddsLimit/BspOddsLimit.web";
import styles from "./ExchangeInlinePlacePanel.web.module.css";
import type {
  ExchangeInlinePlacePanelOnQuickStakeTouch,
  ExchangeInlinePlacePanelViewModel,
} from "./ExchangeInlinePlacePanel.types";

const DEFAULT_NOTIFICATIONS: AlertViewModel[] = [];

export const ExchangeInlinePlacePanel: FunctionComponent<ExchangeInlinePlacePanelViewModel> = ({
  priceInputId,
  sizeInputId,
  currencySymbol,
  hasFreeBets,
  hasPlaceError = false,
  isFreeBetsSelected,
  isPlaceButtonDisabled,
  loadingLabel,
  disabled = false,
  placeBtnLabel,
  pricePlaceholder,
  quickStakes,
  sizePlaceholder,
  sizePlaceholderLay,
  showMultiplier,
  betDelay,
  freeBetsLabel,
  notifications = DEFAULT_NOTIFICATIONS,
  price,
  profitLabel,
  profitValue,
  profitRawValue,
  size,
  focusedInputId,
  outcomeSegments,
  onFreeBetsChange,
  onPriceNudgeDown,
  onPriceNudgeUp,
  onSizeNudgeDown,
  onSizeNudgeUp,
  onPlaceClick,
  onPriceChange,
  onPriceBlur,
  onPriceFocus,
  onQuickStakeTouch,
  onSizeChange,
  onSizeFocus,
  onSizeBlur,
  onPriceMouseDown,
  onSizeMouseDown,
  isMatchMeOn,
  isSpActive,
  isOddsLimitActive,
  oddsLimitTitle,
  startingPrice,
  onOddsLimitToggle,
}) => {
  const handleOnPriceFocus = useCallback(
    (isFocused: boolean): void => {
      if (isFocused && !isMatchMeOn) {
        onPriceFocus();
      }
    },
    [onPriceFocus, isMatchMeOn],
  );

  const handleOnSizeFocus = useCallback(
    (isFocused: boolean): void => {
      if (isFocused) {
        onSizeFocus();
      }
    },
    [onSizeFocus],
  );

  const handleQuickStakeTouch = useCallback<ExchangeInlinePlacePanelOnQuickStakeTouch>(
    (...args) => !disabled && onQuickStakeTouch(...args),
    [disabled, onQuickStakeTouch],
  );

  const isPriceFocused = focusedInputId ? focusedInputId === priceInputId : false;
  const isSizeFocused = focusedInputId ? focusedInputId === sizeInputId : false;
  const computedSizePlaceholder =
    sizePlaceholderLay && !isSizeFocused && size == null ? sizePlaceholderLay : sizePlaceholder;

  return (
    <div className={styles.container}>
      <div className={styles.inputsContainer}>
        <span className={styles.input} onMouseDown={isMatchMeOn ? undefined : onPriceMouseDown}>
          {isSpActive && !isOddsLimitActive ? (
            <FixedNumberInputField value={startingPrice} label={pricePlaceholder} />
          ) : (
            <NudgesNumberInputField
              id={priceInputId}
              disabled={disabled}
              label={pricePlaceholder}
              value={price}
              readonly={isMatchMeOn}
              focused={isPriceFocused}
              hasCaret={isPriceFocused}
              onNudgeUp={onPriceNudgeUp}
              onNudgeDown={onPriceNudgeDown}
              onBlur={onPriceBlur}
              onFocus={handleOnPriceFocus}
              onChange={onPriceChange}
            />
          )}
        </span>

        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <span className={styles.input} onMouseDown={onSizeMouseDown}>
          <NudgesNumberInputField
            id={sizeInputId}
            disabled={disabled}
            label={computedSizePlaceholder}
            value={size}
            currencySymbol={currencySymbol}
            multiplier={showMultiplier || undefined}
            focused={isSizeFocused}
            hasCaret={isSizeFocused}
            onBlur={onSizeBlur}
            onFocus={handleOnSizeFocus}
            onChange={onSizeChange}
            onNudgeUp={onSizeNudgeUp}
            onNudgeDown={onSizeNudgeDown}
          />
        </span>
      </div>
      {outcomeSegments && (
        <div className={styles.outcomeSegments}>
          <OutcomeBetSegments {...outcomeSegments} size={BetSegmentsSize.LARGE} />
        </div>
      )}
      {isSpActive && (
        <div className={styles.freebets}>
          <BspOddsLimit
            disabled={disabled}
            title={oddsLimitTitle}
            isSelected={isOddsLimitActive}
            onChange={onOddsLimitToggle}
          />
        </div>
      )}
      {hasFreeBets && (
        <div className={styles.freebets}>
          <FreeBets
            disabled={disabled}
            label={freeBetsLabel}
            isSelected={isFreeBetsSelected}
            onFreeBetsChange={onFreeBetsChange}
          />
        </div>
      )}
      {notifications?.length ? (
        <div className={styles.notifications}>
          <Alerts alerts={notifications} />
        </div>
      ) : null}
      <div className={styles.placeButton}>
        <PrimaryButton
          stopAnimation={hasPlaceError}
          label={placeBtnLabel}
          delay={betDelay}
          disabled={disabled || isPlaceButtonDisabled}
          loadingLabel={loadingLabel}
          onTap={onPlaceClick}
          secondaryLabel={profitLabel}
        >
          <PNLAndWhatIf
            pnl={profitValue}
            rawPnl={profitRawValue}
            size={PNLAndWhatIfSize.MEDIUM}
            agnostic={true}
            disabled={disabled || isPlaceButtonDisabled}
          />
        </PrimaryButton>
      </div>
      {quickStakes && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className={styles.quickStakes} onMouseDown={(e): void => e.preventDefault()}>
          <QuickStakes quickStakes={quickStakes} onTouch={handleQuickStakeTouch} isDisabled={disabled} />
        </div>
      )}
    </div>
  );
};
